import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminFeedbackApi } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, Pagination, Select, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { FEEDBACK_CATEGORIES, FEEDBACK_STATUSES } from '@shared/constants/feedback.ts';
import { formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * Product Feedback — the operator's queue and dashboard (spec §11, §34, §35).
 *
 * The counters at the top are the whole navigation: an operator opens this
 * page to answer "is there anything waiting for me", and everything else is a
 * filter on the same list underneath.
 *
 * The revenue column is the one thing here with no customer-facing equivalent,
 * and that is deliberate (§35, §36): a vote is a vote on the board, while
 * "seventy-one votes from sixty-three companies representing $14,820 a month"
 * is how a roadmap actually gets decided. It never leaves this console.
 */

const SORTS = [
  { value: 'newest', label: 'Newest' },
  { value: 'votes', label: 'Total votes' },
  { value: 'recent_votes_7d', label: 'Votes — last 7 days' },
  { value: 'recent_votes_30d', label: 'Votes — last 30 days' },
  { value: 'companies', label: 'Companies voting' },
  { value: 'mrr', label: 'MRR represented' },
  { value: 'trending', label: 'Trending' },
];

export default function AdminFeedbackPage() {
  useDocumentTitle('Product Feedback');
  const [params, setParams] = useSearchParams();

  const status = params.get('status') ?? 'pending_moderation';
  const sort = params.get('sort') ?? 'newest';
  const category = params.get('category') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const [search, setSearch] = useState(params.get('q') ?? '');

  const setParam = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    if (!('page' in updates)) next.delete('page');
    setParams(next, { replace: true });
  };

  const analytics = useQuery({
    queryKey: ['admin', 'feedback', 'analytics'],
    queryFn: ({ signal }) => adminFeedbackApi.analytics(signal),
  });

  const list = useQuery({
    queryKey: ['admin', 'feedback', { status, sort, category, q: params.get('q'), page }],
    queryFn: ({ signal }) =>
      adminFeedbackApi.list(
        {
          status,
          sort,
          ...(category ? { category } : {}),
          ...(params.get('q') ? { q: params.get('q')! } : {}),
          page,
          pageSize: 25,
        },
        signal,
      ),
  });

  const counts = analytics.data?.counts;

  return (
    <div className="portal-page">
      <WorkPage
        title="Product Feedback"
        description="Customer feature requests: moderation, development status and prioritization."
        actions={
          <Link className="btn" to="/admin/feedback/settings">
            Settings
          </Link>
        }
      >
        {analytics.isError ? (
          <div className="mb-4">
            <Alert tone="error" title="The dashboard could not be loaded">
              Check that the feedback tables have been migrated.{' '}
              <Button variant="ghost" onClick={() => void analytics.refetch()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : null}

        <div className="mb-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Counter
            label="Pending review"
            value={counts?.pending_moderation}
            onClick={() => setParam({ status: 'pending_moderation' })}
            emphasis
          />
          <Counter label="Published ideas" value={counts?.approved} onClick={() => setParam({ status: 'approved' })} />
          <Counter
            label="In progress"
            value={counts?.in_progress}
            onClick={() => setParam({ status: 'in_progress' })}
          />
          <Counter label="Preview" value={counts?.preview} onClick={() => setParam({ status: 'preview' })} />
          <Counter label="Completed" value={counts?.deployed} onClick={() => setParam({ status: 'deployed' })} />
          <Counter label="Rejected" value={counts?.rejected} onClick={() => setParam({ status: 'rejected' })} />
        </div>

        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Votes this month" value={analytics.data?.votesThisMonth} />
          <Stat label="Companies voting this month" value={analytics.data?.votingCompanies} />
          <Stat label="Possible duplicates" value={counts?.possibleDuplicates} />
          <Stat
            label="Median days idea → shipped"
            value={analytics.data?.medianDaysToShip ?? undefined}
            fallback="Nothing shipped yet"
          />
        </div>

        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Approval rate" value={analytics.data?.approvalRate} suffix="%" />
          <Stat label="Average votes per request" value={analytics.data?.averageVotesPerRequest} />
          <Stat label="Preview sessions" value={analytics.data?.previewSessions} />
          <Stat
            label="Preview satisfaction"
            value={analytics.data?.previewSatisfaction ?? undefined}
            suffix="%"
            fallback="No responses yet"
          />
        </div>

        <form
          className="card mb-6 grid gap-4 sm:grid-cols-4"
          onSubmit={(event) => {
            event.preventDefault();
            setParam({ q: search.trim() || undefined });
          }}
        >
          <Select
            label="Status"
            value={status}
            options={[
              { value: 'all', label: 'All statuses' },
              ...FEEDBACK_STATUSES.map((value) => ({ value, label: value.replace(/_/g, ' ') })),
            ]}
            onChange={(event) => setParam({ status: event.target.value })}
          />
          <Select
            label="Category"
            value={category}
            options={[
              { value: '', label: 'All areas' },
              ...FEEDBACK_CATEGORIES.map((value) => ({ value, label: value.replace(/_/g, ' ') })),
            ]}
            onChange={(event) => setParam({ category: event.target.value })}
          />
          <Select
            label="Sort by"
            value={sort}
            options={SORTS}
            onChange={(event) => setParam({ sort: event.target.value })}
          />
          <div className="flex items-end gap-2">
            <TextInput
              label="Search"
              value={search}
              placeholder="title, description or email"
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="mb-4">
              <Button type="submit">Search</Button>
            </div>
          </div>
        </form>

        {list.isLoading ? <LoadingBlock label="Loading requests…" /> : null}
        {list.isError ? (
          <Alert tone="error" title="The request list could not be loaded">
            <Button variant="ghost" onClick={() => void list.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : null}

        {!list.isLoading && !list.isError && (list.data?.items.length ?? 0) === 0 ? (
          <WorkEmpty title="Nothing matches those filters">
            {status === 'pending_moderation' ? 'The moderation queue is empty.' : 'Try a different status or search.'}
          </WorkEmpty>
        ) : null}

        {list.data && list.data.items.length > 0 ? (
          <TableScroll aria-label="Feedback" className="overflow-x-auto">
            {/* Seven columns need more than a phone's width, and the wrapper
              can only scroll a table allowed to outgrow it. The request
              title is the cell a submitter can make arbitrarily long, so it
              is bounded — otherwise one title decides the width every other
              row has to scroll through. */}
            <table className="data-table w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-[color:var(--color-line)] text-left">
                  <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                    Request
                  </th>
                  <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                    Company
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                    Votes
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                    7d
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                    Companies
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                    MRR
                  </th>
                  <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((row) => (
                  <tr key={row.id} className="border-b border-[color:var(--color-line)] align-top">
                    <td className="py-3 pr-4">
                      <Link
                        to={`/admin/feedback/${row.id}`}
                        className="block max-w-[22rem] table-text font-semibold hover:underline"
                      >
                        {row.title}
                      </Link>
                      <p className="table-text mt-1 max-w-md text-xs text-[color:var(--color-muted)]">
                        {row.status.replace(/_/g, ' ')}
                        {row.category ? ` · ${row.category.replace(/_/g, ' ')}` : ''}
                        {row.duplicateCount > 0 ? ` · ${row.duplicateCount} merged in` : ''}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="block max-w-[18rem] table-text">{row.companyName ?? row.companyId}</span>
                      <span className="block max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                        {row.submittedByEmail}
                      </span>
                    </td>
                    <td className="tabular py-3 pr-4 text-right whitespace-nowrap">{row.votes}</td>
                    <td className="tabular py-3 pr-4 text-right whitespace-nowrap">{row.votesRecent7d}</td>
                    <td className="tabular py-3 pr-4 text-right whitespace-nowrap">{row.companiesVoting}</td>
                    <td className="tabular py-3 pr-4 text-right whitespace-nowrap">
                      {formatCents(row.voterMrrCents, 'USD')}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{formatNumericInstant(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        ) : null}

        <Pagination
          page={page}
          totalPages={list.data?.totalPages}
          onPageChange={(next) => setParam({ page: String(next) })}
        />
      </WorkPage>
    </div>
  );
}

function Counter({
  label,
  value,
  onClick,
  emphasis,
}: {
  label: string;
  value: number | undefined;
  onClick: () => void;
  emphasis?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={emphasis ? 'card text-left border-[color:var(--color-brand)]' : 'card text-left'}
    >
      <span className="block text-2xl font-bold tabular">{value ?? '—'}</span>
      <span className="block text-xs text-[color:var(--color-muted)]">{label}</span>
    </button>
  );
}

function Stat({
  label,
  value,
  suffix,
  fallback,
}: {
  label: string;
  value: number | undefined;
  suffix?: string;
  fallback?: string;
}) {
  return (
    <div className="card">
      <span className="block text-xl font-bold tabular">
        {value === undefined ? (fallback ?? '—') : `${value}${suffix ?? ''}`}
      </span>
      <span className="block text-xs text-[color:var(--color-muted)]">{label}</span>
    </div>
  );
}
